import { useEffect, useState } from "react";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import SliderCard from "../components/Slider";
import { getAuthHeaders } from "../utils/authUtils";
import { generatePageNumbers } from "../utils/pagination";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;

export const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 6,
    total: 0,
    total_pages: 1,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.title = "Events - Smart Scholars 360";
    AOS.init({ duration: 800, once: false });
    fetchEvents();
  }, []);

  const fetchEvents = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_URI}/entities?category=event&page=${page}&limit=${pagination.page_size}`,
        getAuthHeaders(),
      );

      if (res.data && res.data.data) {
        const transformedData = res.data.data.map((item) => ({
          id: item.id,
          title: item.data.title,
          date_day: item.data.date_day,
          date_month_year: item.data.date_month_year,
          description: item.data.description,
          images: item.data.images || [],
        }));

        setEvents(transformedData);
        setPagination({
          page: res.data.page,
          page_size: res.data.limit,
          total: res.data.total,
          total_pages: Math.ceil(res.data.total / res.data.limit),
        });
      } else {
        setEvents([]);
      }
      setError("");
    } catch (err) {
      setError("Failed to load events. Showing default content.");
      setEvents([
        {
          id: 1,
          title: "Green Roots: Community Tree Plantation Drive – Event Recap",
          date_day: "3",
          date_month_year: "July 2025",
          is_new: true,
          description:
            "On July 20, 2025, our community came together for the Green Roots Tree Plantation Drive at Green Valley Public Park. Over 150 volunteers participated, planting more than 200 native trees to promote a greener and healthier environment.",
          images: ["/slider1.jpg", "/slider2.jpg"],
        },
        {
          id: 2,
          title: "Clean Water Mission: Village Outreach Report",
          date_day: "10",
          date_month_year: "June 2025",
          is_new: true,
          description:
            "A successful mission to provide clean water access to remote villages in the eastern region. Volunteers installed 10 water filters impacting over 500 lives.",
          images: ["/slider1.jpg", "/slider2.jpg"],
        },
      ]);
      setPagination({ page: 1, page_size: 6, total: 2, total_pages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages && !loading) {
      fetchEvents(newPage);
      document
        .querySelector(".Event_body")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const EventSkeleton = () => (
    <div className="row Events_content mb-5" style={{ opacity: 0.7 }}>
      <div className="slider col-lg-5 col-12">
        <div
          style={{
            width: "100%",
            height: "300px",
            background:
              "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite",
            borderRadius: "8px",
          }}
        />
      </div>
      <div className="event_info col-lg-5">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            style={{
              width: i === 4 ? "70%" : "100%",
              height: i === 0 ? "24px" : "16px",
              borderRadius: "4px",
              marginBottom: "10px",
              background:
                "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
              backgroundSize: "200% 100%",
              animation: "shimmer 1.5s infinite",
            }}
          />
        ))}
      </div>
    </div>
  );

  if (loading && events.length === 0) {
    return (
      <>
        <div className="ss-inner-hero">
          <div className="container">
            <h1>Events</h1>
            <p>Stay updated with our latest activities and community events</p>
          </div>
        </div>
        <section className="Event_body">
          <div className="container">
            <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
            {Array.from({ length: 3 }).map((_, i) => (
              <EventSkeleton key={i} />
            ))}
          </div>
        </section>
      </>
    );
  }

  if (!loading && events.length === 0) {
    return (
      <>
        <div className="ss-inner-hero">
          <div className="container">
            <h1>Events</h1>
            <p>Stay updated with our latest activities and community events</p>
          </div>
        </div>
        <section className="Event_body">
          <div className="container">
            <div style={{ textAlign: "center", padding: "50px" }}>
              <h3>No events available</h3>
              <p>Check back later for new events!</p>
              {/* <button
                className="btn btn-outline-primary mt-2"
                onClick={() => fetchEvents(1)}
              >
                Try Again
              </button> */}
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <div className="ss-inner-hero">
        <div className="container">
          <h1>Events</h1>
          <p>Stay updated with our latest activities and community events</p>
        </div>
      </div>
      <section className="Event_body">
        <div className="container">
          <style>{`@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>

          {error && (
            <div className="alert alert-warning" role="alert">
              {error}
            </div>
          )}

          {loading && events.length > 0 && (
            <>
              {Array.from({ length: pagination.page_size }).map((_, i) => (
                <EventSkeleton key={i} />
              ))}
            </>
          )}

          {!loading &&
            events.map((event) => (
              <div className="row Events_content mb-5" key={event.id}>
                <div className="slider col-lg-5 col-12">
                  <SliderCard
                    images={event.images || []}
                    customClass="event-slider"
                  />
                </div>
                <div className="event_info col-lg-5">
                  <div className="top">
                    <h5>{event.title || "Untitled Event"}</h5>
                    <p className="event_date">
                      {event.date_day || "N/A"}
                      <span>{event.date_month_year || "No Date"}</span>
                    </p>
                    {event.is_new && (
                      <span className="badge text-bg-primary new_event">
                        New
                      </span>
                    )}
                  </div>
                  <p className="event_description">
                    {event.description || "No description available."}
                  </p>
                </div>
              </div>
            ))}

          {pagination.total_pages > 1 && (
            <div style={{ textAlign: "center", marginTop: "30px" }}>
              <nav aria-label="Event pagination">
                <ul className="pagination justify-content-center">
                  <li
                    className={`page-item ${
                      pagination.page === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1 || loading}
                    >
                      Previous
                    </button>
                  </li>
                  {generatePageNumbers(
                    pagination.page,
                    pagination.total_pages,
                  ).map((page, i) => (
                    <li
                      key={i}
                      className={`page-item ${
                        page === pagination.page ? "active" : ""
                      } ${page === "..." ? "disabled" : ""}`}
                    >
                      {page === "..." ? (
                        <span className="page-link">...</span>
                      ) : (
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                          disabled={loading}
                        >
                          {page}
                        </button>
                      )}
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      pagination.page === pagination.total_pages
                        ? "disabled"
                        : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={
                        pagination.page === pagination.total_pages || loading
                      }
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </section>
    </>
  );
};
